import React, { useCallback, useState } from "react";
import { apiClient } from "@/services/apiClient";
import { useAppStore } from "@/core/store/useAppStore";
import { PlusIcon, MicIcon } from "@/components/icons";
import { Button } from "@/components/ui/Button";
import { Drawer } from "@/components/ui/Drawer";
import { FooterConfirm, useFooterStep } from "@/components/ui/FooterConfirm";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { VoiceRecorder } from "@/core/voice";

interface AddNotePopoverProps {
  clientId: string;
  onNoteSaved?: () => void;
  /** "panel" renders the standard panel-header action trigger. */
  variant?: "icon" | "panel";
  /** Enables the mic: the recorded clip is handed over here and the drawer closes. */
  onSendVoice?: (blob: Blob, mimeType: string, durationSeconds: number) => void;
}

/** Where to go once an unsent recording is confirmed discarded. */
type Leave = "close" | "type";

/**
 * "New Note", from the workspace header and the Notes panel. It opens in the
 * shared Drawer (DD-051): the floating right panel on desktop, the bottom
 * sheet on phones. Leaving voice mode with an unsent recording asks in the
 * drawer's footer (DD-045), never in a dialog over the drawer.
 */
export const AddNotePopover: React.FC<AddNotePopoverProps> = ({
  clientId,
  onNoteSaved,
  variant = "icon",
  onSendVoice,
}) => {
  const { triggerNotesRefresh } = useAppStore();
  const addToast = useAppStore((s) => s.addToast);
  const [isOpen, setIsOpen] = useState(false);
  const [content, setContent] = useState("");
  const [tags, setTags] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [mode, setMode] = useState<"text" | "voice">("text");
  const [hasUnsentRecording, setHasUnsentRecording] = useState(false);
  const [pendingLeave, setPendingLeave] = useState<Leave | null>(null);
  const footerStep = useFooterStep<Leave>(null, { step: pendingLeave, go: setPendingLeave });

  // Once the take is gone (deleted, sent, or the recorder unmounted) there is nothing left to ask about.
  // Stable, because the recorder re-reports whenever this callback changes.
  const handleUnsentChange = useCallback((hasUnsent: boolean) => {
    setHasUnsentRecording(hasUnsent);
    if (!hasUnsent) setPendingLeave(null);
  }, []);

  const handleSave = async () => {
    if (!content.trim()) return;

    try {
      setIsSaving(true);
      const tagArray = tags
        .split(",")
        .map((t) => t.trim())
        .filter((t) => t);

      await apiClient.post(`/client/${clientId}/notes`, {
        content: content.trim(),
        tags: tagArray,
      });

      setContent("");
      setTags("");
      setIsOpen(false);
      triggerNotesRefresh();
      if (onNoteSaved) onNoteSaved();
    } catch (error) {
      console.error("Failed to save note:", error);
      addToast({ type: "error", message: "Failed to save note", duration: 6000 });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setContent("");
    setTags("");
    setIsOpen(false);
    setMode("text");
  };

  const handleSendVoice = (blob: Blob, mimeType: string, durationSeconds: number) => {
    onSendVoice?.(blob, mimeType, durationSeconds);
    handleCancel();
  };

  const leave = (to: Leave) => (to === "close" ? handleCancel() : setMode("text"));

  /** Leaving voice mode drops an unsent recording, which can run 60 minutes: ask in the footer first. */
  const requestLeave = (to: Leave) => {
    if (mode === "voice" && hasUnsentRecording) footerStep.ask(to);
    else leave(to);
  };

  const handleDiscardConfirm = () => {
    const to = pendingLeave;
    setPendingLeave(null);
    if (to) leave(to);
  };

  let footer: React.ReactNode = null;
  if (pendingLeave) {
    footer = (
      <FooterConfirm
        confirmLabel="Discard"
        onConfirm={handleDiscardConfirm}
        onKeep={footerStep.leave}
        pending={false}
        loading={false}
        stacked
      >
        Discard this recording? It hasn't been sent and can't be recovered.
      </FooterConfirm>
    );
  } else if (mode === "text") {
    footer = (
      <>
        <Button
          type="button"
          size="sm"
          onClick={() => void handleSave()}
          disabled={!content.trim()}
          isLoading={isSaving}
        >
          Save Note
        </Button>
      </>
    );
  }

  return (
    <>
      {/* Trigger button */}
      {variant === "panel" ? (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          title={ENTITY_CONFIG.note.createLabel}
          className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
          {ENTITY_CONFIG.note.createLabel}
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
          title={ENTITY_CONFIG.note.createLabel}
        >
          <PlusIcon className="w-5 h-5" />
        </button>
      )}

      {/* X, Escape and a tap outside all arrive here: each asks before dropping a take. */}
      <Drawer
        isOpen={isOpen}
        onClose={() => requestLeave("close")}
        title={ENTITY_CONFIG.note.createLabel}
        footer={footer}
        bodyPadding
        a11yDescription="A new note for this client."
        className="md:max-w-md"
      >
        {mode === "voice" ? (
          <div className="space-y-3">
            <VoiceRecorder onSend={handleSendVoice} onUnsentChange={handleUnsentChange} />
            <button
              type="button"
              ref={footerStep.backRef("type")}
              onClick={() => requestLeave("type")}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Type instead
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">Note Content</label>
                {onSendVoice && (
                  <button
                    type="button"
                    onClick={() => setMode("voice")}
                    title="Record a voice note"
                    className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700"
                  >
                    <MicIcon className="w-4 h-4" />
                    Record
                  </button>
                )}
              </div>
              {/* Remounts on each open and after a trip to the recorder: start from the draft. */}
              <RichTextEditor
                format="markdown"
                initialContent={content}
                onContentChange={setContent}
                placeholder="Save an idea, strategy, or insight..."
                autoFocus
                className="min-h-[8rem] rounded-lg border border-gray-300 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tags (optional)
              </label>
              <input
                type="text"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="idea, strategy, content"
                className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="mt-1 text-xs text-gray-500">
                Separate tags with commas
              </p>
            </div>
          </div>
        )}
      </Drawer>
    </>
  );
};
